/**
 * Rank courses for "where to resume" — the single source of truth shared by the
 * Home current-course fallback (`useMostRecentCourse`) and the "Pick up where you
 * left off" suggestions (`CourseHighlight`), so the two surfaces can't drift.
 *
 * Drops finished courses (progress 100), then sorts by completed-lesson recency
 * (the course of the most recently completed lesson first — `completedLessons` is
 * most-recent-first), with progress descending as the tiebreaker. iOS parity.
 *
 * Generic over the course shape so each caller keeps its own fields; only
 * `courseId` + `progress` are read.
 */
export function rankCoursesByRecency<
  T extends { courseId: number; progress: number },
>(courses: T[], completedLessons: { courseId: number }[] | undefined): T[] {
  // courseId → recency index; first occurrence in completedLessons wins
  // (lower index = more recently worked).
  const recencyByCourseId = new Map<number, number>();
  completedLessons?.forEach((lesson, index) => {
    if (!recencyByCourseId.has(lesson.courseId)) {
      recencyByCourseId.set(lesson.courseId, index);
    }
  });

  return [...courses]
    .filter((course) => course.progress < 100)
    .sort((a, b) => {
      const aRecency =
        recencyByCourseId.get(a.courseId) ?? Number.MAX_SAFE_INTEGER;
      const bRecency =
        recencyByCourseId.get(b.courseId) ?? Number.MAX_SAFE_INTEGER;
      if (aRecency !== bRecency) return aRecency - bRecency;
      return b.progress - a.progress;
    });
}
